import { type ServiceParams, unwrapJson } from "../client";
import type {
  ActivityItem,
  ActivitySeverity,
  DashboardMetrics,
} from "./types";

/**
 * Dashboard aggregator. The frontend fans out to six backend
 * endpoints in parallel via ``Promise.allSettled`` so any single
 * source going dark only blacks out its own card. Each per-source
 * status is reported back on ``DashboardMetrics.source_status``.
 *
 * Backend endpoints consumed (paths confirmed against the API):
 *   - GET /v1/governance/observability/posture/summary
 *   - GET /v1/workspaces
 *   - GET /v1/governance/observability/inference/latency?window=24h
 *   - GET /v1/governance/observability/policy-enforcement?window=24h
 *   - GET /v1/governance/risk/snapshots/latest
 *   - GET /v1/audit/logs?limit=5
 */
const Dashboard = ({ url, config }: ServiceParams) => ({
  async getMetrics(workspaceId?: string): Promise<DashboardMetrics> {
    const wsQuery = workspaceId
      ? `?workspace_id=${encodeURIComponent(workspaceId)}`
      : "";
    const wsAmp = workspaceId
      ? `&workspace_id=${encodeURIComponent(workspaceId)}`
      : "";

    const headers = config();
    const get = (path: string) =>
      fetch(`${url}${path}`, { method: "GET", headers }).then((r) =>
        unwrapJson<Record<string, unknown>>(r)
      );

    const [posture, workspaces, latency, policy, risk, audit] =
      await Promise.allSettled([
        get(`/v1/governance/observability/posture/summary${wsQuery}`),
        get(`/v1/workspaces?limit=200`),
        get(
          `/v1/governance/observability/inference/latency?window=24h${wsAmp}`
        ),
        get(
          `/v1/governance/observability/policy-enforcement?window=24h${wsAmp}`
        ),
        get(`/v1/governance/risk/snapshots/latest${wsQuery}`),
        get(`/v1/audit/logs?limit=5${wsAmp}`),
      ]);

    return {
      governance_posture_pct: _extractPosturePct(posture),
      governance_posture_band: _extractPostureBand(posture),
      active_workspaces_count: _extractWorkspacesCount(workspaces),
      total_requests_24h: _extractRequestCount(latency),
      policy_decisions_count_24h: _extractPolicyDecisions(policy),
      risk_score_overall: _extractRiskScore(risk),
      risk_score_band: _extractRiskBand(risk),
      recent_activity: _extractActivity(audit),
      source_status: {
        posture: posture.status === "fulfilled" ? "ok" : "error",
        workspaces: workspaces.status === "fulfilled" ? "ok" : "error",
        requests: latency.status === "fulfilled" ? "ok" : "error",
        policy: policy.status === "fulfilled" ? "ok" : "error",
        risk: risk.status === "fulfilled" ? "ok" : "error",
        activity: audit.status === "fulfilled" ? "ok" : "error",
      },
    };
  },
});

// ============================================================================
// Per-source extractors — defensive against schema drift
// ============================================================================

type Settled<T> = PromiseSettledResult<T>;

function _valueOr<T>(
  settled: Settled<Record<string, unknown>>,
  fallback: T,
  fn: (v: Record<string, unknown>) => T
): T {
  if (settled.status !== "fulfilled") return fallback;
  try {
    return fn(settled.value);
  } catch {
    return fallback;
  }
}

function _extractPosturePct(
  s: Settled<Record<string, unknown>>
): number | null {
  return _valueOr(s, null, (v) => {
    const overall = (v as { overall_posture?: { score?: number } })
      .overall_posture;
    if (overall?.score != null) return Number(overall.score);
    const direct = (v as { overall_score?: number }).overall_score;
    return direct != null ? Number(direct) : null;
  });
}

function _extractPostureBand(
  s: Settled<Record<string, unknown>>
): string | null {
  return _valueOr(s, null, (v) => {
    const overall = (v as { overall_posture?: { band?: string } })
      .overall_posture;
    return overall?.band ?? null;
  });
}

function _extractWorkspacesCount(
  s: Settled<Record<string, unknown>>
): number {
  return _valueOr(s, 0, (v) => {
    const total = (v as { total?: number }).total;
    if (typeof total === "number") return total;
    const items = (v as { items?: unknown[] }).items;
    return Array.isArray(items) ? items.length : 0;
  });
}

function _extractRequestCount(
  s: Settled<Record<string, unknown>>
): number {
  return _valueOr(s, 0, (v) => {
    const total = (v as { total_requests?: number }).total_requests;
    return typeof total === "number" ? total : 0;
  });
}

function _extractPolicyDecisions(
  s: Settled<Record<string, unknown>>
): number {
  return _valueOr(s, 0, (v) => {
    const total = (v as { total_decisions?: number }).total_decisions;
    return typeof total === "number" ? total : 0;
  });
}

function _extractRiskScore(
  s: Settled<Record<string, unknown>>
): number | null {
  return _valueOr(s, null, (v) => {
    const score = (v as { overall_score?: number }).overall_score;
    return score != null ? Number(score) : null;
  });
}

function _extractRiskBand(
  s: Settled<Record<string, unknown>>
): string | null {
  return _valueOr(s, null, (v) => {
    const band = (v as { score_band?: string }).score_band;
    return band ?? null;
  });
}

interface AuditLogEntry {
  id: string;
  created_at?: string;
  timestamp?: string;
  user_email?: string;
  actor_email?: string;
  action: string;
  status?: string;
  severity?: ActivitySeverity;
}

function _extractActivity(
  s: Settled<Record<string, unknown>>
): ActivityItem[] {
  return _valueOr(s, [], (v) => {
    const items =
      (v as { items?: AuditLogEntry[]; logs?: AuditLogEntry[] }).items ??
      (v as { logs?: AuditLogEntry[] }).logs ??
      [];
    return items.slice(0, 5).map((log) => ({
      id: log.id,
      timestamp: log.created_at ?? log.timestamp ?? new Date().toISOString(),
      actor: log.user_email ?? log.actor_email ?? "system",
      action: log.action,
      severity: _normalizeSeverity(log.severity, log.status),
    }));
  });
}

function _normalizeSeverity(
  severity: ActivitySeverity | undefined,
  status: string | undefined
): ActivitySeverity {
  if (severity === "critical" || severity === "warning" || severity === "info") {
    return severity;
  }
  if (status === "blocked" || status === "error") return "warning";
  return "info";
}

export default Dashboard;
